import { FormikProps } from "formik"
import { reaction, type IReactionDisposer } from "mobx"
import { observer } from "mobx-react"
import { Money } from "modules/common/components/money/Money"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { CollapseExpandAll } from "modules/common/components/table/CollapseExpandAll"
import { texts } from "modules/common/texts"
import {
  fields,
  OrderExternalParams,
  OrderSummarySnapshotType,
  OrderSummaryType,
} from "modules/orders-manage/models/order"
import { OrdersStoreType } from "modules/orders-manage/models/orders-store"
import { IdFactory, UploaderFatory } from "modules/orders-manage/types"
import { EmployerDictionaryType } from "modules/spending/employee/models/employee-dictionary"
import React from "react"
import { Schema } from "yup"
import { OtherSpendingFactory, OtherSpendings } from "./other/OtherSpendings"
import { OutsourcerPaymentFactory } from "./outsourced/OutsourcedSpendingRow"
import { OutsourcedSpendingFactory, OutsourcedSpendings } from "./outsourced/OutsourcedSpendings"
import { OwnSpendingFactory, OwnSpendings } from "./own/OwnSpendings"
import { OwnTripSpendings, TripSpendingFactory } from "./ownTripSpendings/OwnTripSpendings"
import { OrderPremium } from "./premium/Premium"
import styles from "./Spendings.module.scss"
import { RevealElement } from "./helpers"
import { MakeGroupKey } from "./other/OtherSpendingsObject"
import { EMPTY_OBJECT_ID } from 'modules/common/constants'
import { createSpendingSaver } from "./spending-saver"
import DenyNavigation from "modules/common/components/routing/DenyNavigation"
import { routes } from "modules/common/routes"
import { withNavigation, NavigationProps } from "modules/common/components/routing/withNavigation"

/** Секция «Состава затрат» — ключ в `spendingCollapse` и в параметре ссылки `expanded` */
type SpendingSection = "outsource" | "other" | "trip" | "own" | "premium"

class Base extends React.Component<SpendingsProps & NavigationProps> {
  static whyDidYouRender = false;
  private me = React.createRef<HTMLDivElement>();
  /** Сохранение отдельной строки затрат и отдельного платежа */
  private saver = createSpendingSaver(this.props.store);

  printable = React.createRef<HTMLDivElement>();
  /** Ссылка, по которой уже раскрыли: дальше сворачивает и раскрывает пользователь */
  private revealed = "";
  private disposeReveal: IReactionDisposer | null = null;

  async componentDidMount() {
    // договор и затраты догружаются после вкладки, а начальное состояние групп родитель
    // сбрасывает при загрузке — раскрываем, только когда загрузка закончилась
    const { store } = this.props
    this.disposeReveal = reaction(
      () => [
        store.loading,
        store.details.id,
        store.details.outsourcedOrderSpendins.length,
        store.details.otherOrderSpendings.length,
        store.details.tripOrderSpendings.length,
      ],
      () => this.revealLinked(),
      { fireImmediately: true }
    )

    if (this.props.orderId !== EMPTY_OBJECT_ID) {
      await this.props.store.loadFinanceActPayments(this.props.orderId)
    }
  }

  componentDidUpdate(prev: SpendingsProps & NavigationProps) {
    // переход по другой ссылке на открытой вкладке или возврат на вкладку
    if (prev.location.hash !== this.props.location.hash || prev.isActiveTab !== this.props.isActiveTab) {
      this.revealLinked()
    }
  }

  componentWillUnmount() {
    this.disposeReveal?.()
  }

  /**
   * Платёж или строка затрат из ссылки `#tab=spending&expanded=<секция>&rowId=<платёж | строка>` — из фин.
   * запросов, сводки расходов, платежей на дашборде, карточки контрагента, списка ошибок, мешающих завершению.
   * Секцию определяем по тому, где платёж (строка) нашёлся (фин. запрос по командировке приходит с «прочими»),
   * раскрываем её и группу, прокручиваем к платежу (строке) и подсвечиваем. Без них — только раскрываем секцию
   */
  revealLinked = () => {
    const { store, orderId, isActiveTab, location } = this.props
    const { rowId, expanded } = OrderExternalParams(location.hash)
    const key = `${rowId}|${expanded}`

    if ((!rowId && !expanded) || this.revealed === key || !isActiveTab) {
      return
    }

    // договор ещё грузится или в сторе прежний
    if (store.loading || store.details.id !== orderId) {
      return
    }

    const details = store.details
    let section = expanded as SpendingSection | ""
    let found = false
    let spendingRow = false

    if (rowId) {
      const hasPayment = (spending: {
        planPayments: { guid: string }[]
        actualPayments: { guid: string; correctionPayments?: { guid: string }[] }[]
      }) =>
        spending.planPayments.some((p) => p.guid === rowId) ||
        spending.actualPayments.some(
          (p) => p.guid === rowId || (p.correctionPayments || []).some((c) => c.guid === rowId)
        )

      const outsourced = details.outsourcedOrderSpendins.find(hasPayment)
      const other = outsourced ? undefined : details.otherOrderSpendings.find(hasPayment)
      const trip = outsourced || other ? undefined : details.tripOrderSpendings.find(hasPayment)

      if (outsourced) {
        section = "outsource"
        store.outsourceCollapser.set(outsourced.contentGuid, true)
      } else if (other) {
        section = "other"
        store.otherCollapser.set(MakeGroupKey(other.comment), true)
      } else if (trip) {
        section = "trip"
        // группа командировок — раздел с сотрудниками, ключ «<название> <guid раздела>»
        const group = details.timesheetSpendings.spendingsMap.find((g) => g.label.endsWith(` ${trip.contentGuid}`))
        if (group) {
          store.rowTripCollapser.set(group.label, true)
        }
      }

      found = !!(outsourced || other || trip)

      // ссылка на саму строку затрат (ошибки, мешающие завершению), а не на платёж
      if (!found) {
        const outsourcedRow = details.outsourcedOrderSpendins.find((s) => s.id === rowId)
        const otherRow = outsourcedRow ? undefined : details.otherOrderSpendings.find((s) => s.id === rowId)

        if (outsourcedRow) {
          section = "outsource"
          store.outsourceCollapser.set(outsourcedRow.contentGuid, true)
        } else if (otherRow) {
          section = "other"
          store.otherCollapser.set(MakeGroupKey(otherRow.comment), true)
        }

        spendingRow = !!(outsourcedRow || otherRow)
      }
    }

    if (!section) {
      return
    }

    this.revealed = key
    store.setupCollapseState(section)

    if (found) {
      RevealElement(this.me, rowId)
    } else if (spendingRow) {
      RevealElement(this.me, rowId, { attr: "data-spending-id" })
    } else {
      RevealElement(this.me, `section-${section}`, { flash: false })
    }
  }

  /** переключение вкладок внутри договора предупреждением не считаем */
  skipNavigationPromt = (location: { pathname: string }) => {
    return location.pathname.startsWith(routes.orders.base(this.props.orderId));
  };

  printOrderFile = (outsourcerId: string, contentGuid: string, comment?: string) => {
    const { store } = this.props
    store.printOutsourcerOrder(outsourcerId, contentGuid, comment)
  };

  render() {
    const {
      ownSpendingFactory,
      paymentFactory,
      otherSpendingFactory,
      outsourcedSpendingFactory,
      tripSpendingFactory,
      newIdd,
    } = this.props
    const { formProps, readOnly, baseUrl, newId, upload, employee, store, schema, isActiveTab, details } = this.props
    const { factMoney } = store.details.timesheetSpendings.spendingsTotal
    if (!isActiveTab) {
      return <div className={styles.spendings}></div>
    }

    const outsourcerOrderTotal = formProps.values.outsourcedOrderSpendins.reduce(
      (acc, unit) => +unit.actualSum + acc,
      0
    )

    const sumPremiums = () => {
      const { store } = this.props
      let assignedSum = 0
      Object.values(store.premiums.premiumProductionAsMap).forEach((premiums) => {
        premiums.forEach((p) => {
          assignedSum += p.sum
        }, 0)
      })
      assignedSum += store.premiums.engineerPremium.sum
      return assignedSum
    }

    const outsourcerActualTotal = formProps.values.outsourcedOrderSpendins.reduce((acc, unit) => {
      return (
        acc + unit.actualPayments.reduce((t, p) => t + p.sum - p.correctionPayments.reduce((s, c) => s + c.sum, 0), 0)
      )
    }, 0)
    const outsourcerLeftTotal = outsourcerOrderTotal - Math.round(outsourcerActualTotal * 100) / 100

    const otherOrderTotal = formProps.values.otherOrderSpendings.reduce((acc, unit) => +unit.actualSum + acc, 0)
    const otherActualTotal = formProps.values.otherOrderSpendings.reduce((acc, unit) => {
      return (
        acc + unit.actualPayments.reduce((t, p) => t + p.sum - p.correctionPayments.reduce((s, c) => s + c.sum, 0), 0)
      )
    }, 0)
    const otherLeftTotal = otherOrderTotal - Math.round(otherActualTotal * 100) / 100
    const premiums = sumPremiums()
    // собственные силы планируются в плане работ — тот же итог показан в шапке своего блока
    const ownOrderTotal = store.details.workloadSpendings.totalPlannedSpending
    const ownActualTotal = factMoney

    const premiumsActualTotal = store.premiums.paidSum

    const orderTotal = outsourcerOrderTotal + otherOrderTotal + ownOrderTotal

    const actualTotal = outsourcerActualTotal + otherActualTotal + ownActualTotal
    const actualTotalPremium = outsourcerActualTotal + otherActualTotal + ownActualTotal + premiumsActualTotal
    const leftTotal = outsourcerLeftTotal + otherLeftTotal

    const orderStatusId = (formProps.values as any)[fields.orderStatusId]
    const cantEditFinanceValues = !orderStatusId || store.statuses.canEditOrderFinanceFields(orderStatusId)
    const cantAssingPremium = store.statuses.canAssingPremium(store.details.orderStatusId)
    const canEditDistribution = !cantAssingPremium

    const unsaved = this.saver.hasUnsaved(formProps.values as any)

    return (
      <div className={styles.spendings} ref={this.me}>
        {/* предупреждаем об уходе, только если действительно есть неотправленные строки
            или платежи: остальное на этой вкладке сохраняется сразу */}
        <DenyNavigation
          when={unsaved}
          message="В составе затрат остались несохраненные строки или оплаты"
          skip={this.skipNavigationPromt}
        />

        <div className="planr-tools">
          <PlanrButton type="graybtn" icon="general-contact" round={true} title="Сформировать лист контактов" />

          <PlanrButton
            type="graybtn"
            icon="general-print"
            round={true}
            title={texts.print}
            onClick={() => {
              store.printSpendingsTab(formProps.values)
            }}
          />

          <CollapseExpandAll onChange={this.collapseAll} />
        </div>

        <div
          ref={this.printable}
          className={styles.inner}
        //     style={premiumsOrderTotal > 0 || premiumsActualTotal > 0 ? { paddingTop: "45px" } : {}}
        >
          <div className={styles.spendingsTotal}>
            <div>
              <div className="order-total" style={{ display: "flex", flexDirection: "column" }}>
                {premiums > 0 ? (
                  <div>
                    Всего в т.ч. премии:&nbsp;
                    <Money className="spendings-money" amount={orderTotal + premiums} />
                  </div>
                ) : (
                  <div>
                    Всего по договору:&nbsp;
                    <Money className="spendings-money" amount={orderTotal} />
                  </div>
                )}
              </div>
            </div>

            <div
              className={`actual-total ${leftTotal > 0 ? "red-total" : "green-total"}`}
              style={{ display: "flex", flexDirection: "column" }}
            >
              {premiumsActualTotal > 0 ? (
                <div>
                  По оплатам (в т.ч. премии):&nbsp;
                  <Money className="spendings-money" amount={actualTotalPremium} />
                </div>
              ) : (
                <div>
                  Всего по оплатам:&nbsp;
                  <Money className="spendings-money " amount={actualTotal} />
                </div>
              )}
            </div>
            <div className={`spendings-total ${leftTotal !== 0 ? "red-total" : "green-total"}`}>
              Остаток по оплатам:&nbsp;
              <Money className="spendings-money" amount={leftTotal} />
            </div>
          </div>
          <div data-object-id="section-outsource" className={styles.section}>
            <OutsourcedSpendings
              saver={this.saver}
              disabled={!cantEditFinanceValues}
              agents={store.agents}
              name={fields.outsourcedOrderSpendins}
              paymentFactory={paymentFactory}
              spendingFactory={outsourcedSpendingFactory}
              collapsed={store.spendingCollapse.outsource}
              onToggleCollapse={() => store.toggleCollapse("outsource")}
              innerCollapser={store.outsourceCollapser}
              toggleStatus={store.togglePaymentStatus}
              readOnly={readOnly}
              baseUrl={baseUrl}
              newId={newId}
              upload={upload}
              printOrderFile={store.details.isNewlyCreated ? undefined : this.printOrderFile}
              details={details}
              newIdd={newIdd}
              dirty={formProps.dirty}
            />
          </div>

          <div data-object-id="section-other" className={styles.section}>
            <OtherSpendings
              saver={this.saver}
              disabled={!cantEditFinanceValues}
              agents={store.agents}
              name={fields.otherOrderSpendings}
              paymentFactory={paymentFactory}
              collapsed={store.spendingCollapse.other}
              factory={otherSpendingFactory}
              onToggleCollapse={(value?: boolean) => {
                typeof value === "undefined" ? store.toggleCollapse("other") : store.setupCollapseState("other")
              }}
              toggleStatus={store.togglePaymentStatus}
              readOnly={readOnly}
              innerCollapser={store.otherCollapser}
              baseUrl={baseUrl}
              newId={newId}
              upload={upload}
              printOrderFile={store.details.isNewlyCreated ? undefined : this.printOrderFile}
              details={details}
              newIdd={newIdd}
              dirty={formProps.dirty}
            />
          </div>

          <div data-object-id="section-own" className={styles.section}>
            <OwnSpendings
              disabled={!cantEditFinanceValues}
              factory={ownSpendingFactory}
              name={fields.ownOrderSpendings}
              employee={employee.asMap}
              collapsed={store.spendingCollapse.own}
              onToggleCollapse={() => store.toggleCollapse("own")}
              spendings={store.details.timesheetSpendings}
              planSpendings={store.details.workloadSpendings}
              readOnly={readOnly}
              planCollapser={store.ownPlanCollapser}
              actualCollapser={store.ownActualCollapser}
            />
          </div>

          <div data-object-id="section-trip" className={styles.section}>
            <OwnTripSpendings
              saver={this.saver}
              disabled={!cantEditFinanceValues}
              tripSpendingFactory={tripSpendingFactory}
              factory={ownSpendingFactory}
              name={fields.tripOrderSpendings}
              paymentFactory={paymentFactory}
              ownSpendingName={fields.ownOrderSpendings}
              employee={employee.asMap}
              collapsed={store.spendingCollapse.trip}
              onToggleCollapse={() => store.toggleCollapse("trip")}
              spendings={store.details.timesheetSpendings}
              readOnly={readOnly}
              upload={upload}
              actualCollapser={store.rowTripCollapser}
              baseUrl={baseUrl}
              toggleStatus={store.togglePaymentStatus}
            />
          </div>

          <div data-object-id="section-premium" className={styles.section}>
            <OrderPremium
              collapsed={store.spendingCollapse.premium}
              onToggleCollapse={() => store.toggleCollapse("premium")}
              readOnly={readOnly}
              bank={store.details.premiumBank}
              store={store.premiums}
              name={fields.employeeFeedback}
              canAssing={cantAssingPremium}
              cantEdit={canEditDistribution}
              schema={schema}
              formProps={formProps}
              hide={false}
              premiums={premiums}
            />
          </div>
        </div>
      </div>
    )
  }

  collapseAll = (state: boolean) => {
    const { store } = this.props
    store.spendingCollapse.setAll(state)
    store.otherCollapser.setAll(state)
    store.outsourceCollapser.setAll(state)
    store.ownPlanCollapser.setAll(state)
  };
}

export const Spendings = withNavigation(observer(Base))

interface SpendingsProps extends UploaderFatory, IdFactory {
  formProps: FormikProps<OrderSummarySnapshotType>
  paymentFactory: OutsourcerPaymentFactory
  ownSpendingFactory: OwnSpendingFactory
  otherSpendingFactory: OtherSpendingFactory
  outsourcedSpendingFactory: OutsourcedSpendingFactory
  tripSpendingFactory: TripSpendingFactory
  employee: EmployerDictionaryType
  store: OrdersStoreType
  readOnly: boolean
  schema: Schema<any>
  baseUrl: string
  isActiveTab: boolean
  orderId: string
  details: OrderSummaryType
  newIdd: () => Promise<string>
}
